import React, { FC, useContext } from "react";
import styles from "./AIButton.module.scss";
import { IconButton, Dialog, DialogTitle, DialogContent } from "@mui/material";
import EXTMarkdown from "../EXTMarkdown";
import moment from "moment";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";
import AIDialogProcess from "./components/AIDialogProcess";
import { IS_LOCALHOST, IS_SERVER, TOKEN_COOKIE } from "constant/index";
import { getCookieByKey, Cookies } from "utils";
import { toast } from "react-toastify";
import { AuthProvider } from "pages";
import { FEATURE_KEY } from "constant";
import { useAxios } from "myAxios";
import MarkdownToPPTBtn from "../MarkdownToPPT";
import { useMetadataByCategory } from "components/summary/hooks";
import useSWR from "swr";
import { fetchPrompts } from "components/ai-chat copy/store/PromptActions";
import { useDataActions } from "./DataActions";

interface AccountInfo {
  accountId: string;
  accountName: string;
  accounkey: string;
}

interface AIButtonBaseProps {
  size?: "small" | "large" | "default";
  promptCode?: string;
  model?: string;
  children?: React.ReactNode;
  showPptBtn?: boolean;
  dialogTitleText?: string;
  tooltipTitle?: React.ReactNode;
}

type AIButtonProps =
  | (AIButtonBaseProps & {
      account: AccountInfo;
      id?: never;
      hanldData?: never;
    })
  | (AIButtonBaseProps & {
      account?: never;
      id: string;
      hanldData: () => Promise<string> | string;
    });

interface ListItem {
  id: string;
  content: string;
  createTime: string;
}

const DATA_TYPE_OPTIONS: string[] = [
  "Account Details",
  "Subscription Details",
  "Success Plan Details",
  "Recent Activities",
  "Technical Support Cases",
  "Webex Meeting Usage",
  "Webex Calling Usage",
  "Webex Messaging Usage",
] as const;

const SizeMapping = {
  small: 20,
  default: 24,
  large: 32,
};

// 组件需要基于accountId进行数据请求和缓存，若不基于则传入id和hanldData进行数据请求和缓存
// The component needs to make data requests and localStorage based on accountId. If not, pass in id and hanldData for data requests and localStorage.
const AIButton: FC<AIButtonProps> = (props) => {
  const {
    account,
    id,
    hanldData,
    promptCode,
    model,
    children,
    showPptBtn = false,
    dialogTitleText = "AI Generated Account Profile",
    tooltipTitle = "AI Summary",
    size = "default",
  } = props;
  const const_prompt_code = promptCode || "Account Summary Profile";
  const [content, setContent] = React.useState<string>("");
  const [open, setOpen] = React.useState<boolean>(false);
  const [state, setState] = React.useState<"error" | "thinking" | "loading" | "complete">(
    "complete"
  );
  const authDetail = useContext(AuthProvider);
  const [progressing, setProgressing] = React.useState<number>(0);
  const [createTime, setCreateTime] = React.useState<string>("");
  const generatingRef = React.useRef(false);
  // const [error, setError] = React.useState<string>("ERR_CONNECTION_FAILED");
  // const { data: aiPromptsData } = useMetadataByCategory("AI_PROMPTS");
  const axios = useAxios();
  const { data: aiPromptsData } = useSWR(
    "/api/ai-chat/prompts?promptType=system",
    () =>
      fetchPrompts(axios, {
        pageSize: 50,
        pageNum: 0,
        promptType: "system",
      }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 60000,
    }
  );
  const { data: aiModelListData } = useMetadataByCategory("AI_MODEL");
  const { loadAccountData } = useDataActions(axios);
  const promptContent = aiPromptsData?.prompts?.find(
    (item) => item.promptTitle === const_prompt_code
  )?.promptContent;
  const contentPrefix =
    `Answer the following questions based on these data sets. \n\n` + (promptContent || "");
  const accountPayload = account
    ? {
        ...account,
        accountKey: account.accounkey,
      }
    : undefined;
  const currentId = id || account?.accountId;
  const canHandleAddAccount = Boolean(currentId && (hanldData || account?.accountId));

  const streamAction = async (payload: any) => {
    const token = IS_SERVER ? getCookieByKey(TOKEN_COOKIE, "") : Cookies.get(TOKEN_COOKIE);
    const response = await fetch(
      !IS_LOCALHOST
        ? "/csdigital/v1/accounts/ai-chat/stream"
        : "/api/csdigital/v1/accounts/ai-chat/stream",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }
    );
    const reader: any = response.body?.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let output = "";
    while (true) {
      const { value, done } = await reader?.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      setState("loading");
      for (let line of lines) {
        if (line.startsWith("data:")) {
          line = line.replace(/^data:\s*/g, "");
          try {
            const json = JSON.parse(!!line ? line : "[]");
            const delta = json?.choices?.[0]?.delta;
            const con = delta?.content;
            if (con) {
              output += con;
              setContent(output);
            }

            if (json?.choices?.[0]?.finish_reason === "stop") {
            }
          } catch (e) {
            console.error("error:", e, line);
          }
        }
      }
    }
    setState("complete");
    return output;
  };

  const handleAddAccount = async () => {
    if (generatingRef.current) return;
    setProgressing(0);
    if (!canHandleAddAccount) return;
    generatingRef.current = true;
    setState("thinking");
    setTimeout(() => setProgressing(30), 0);
    try {
      const dataContent = hanldData
        ? await Promise.resolve(hanldData())
        : await (async () => {
            const newTag = {
              id: accountPayload!.accountId,
              account: accountPayload!,
              options: DATA_TYPE_OPTIONS.map((opt) => ({
                option: opt,
                loaded: false,
              })),
            };
            const loadedAccountTag = await loadAccountData(newTag);
            return loadedAccountTag.options
              .map(
                (opt) =>
                  `** JSON Data for The Account ${account?.accountName}: ${opt.option} (${opt.option})** \n ` +
                  "```json\n" +
                  JSON.stringify(opt.data) +
                  "```"
              )
              .join("\n\n");
          })();
      const content = contentPrefix + dataContent;
      setProgressing(85);
      const messages = [
        {
          role: "user",
          content,
        },
      ];
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const result = await streamAction({
            messages,
            max_tokens: 1000,
            temperature: 0.7,
            model: model || aiModelListData?.[aiModelListData.length - 1]?.value,
          });
          const item: ListItem = {
            id: currentId || "",
            content: result || "",
            createTime: moment().tz("America/Los_Angeles").format("MMM Do, YYYY(PT)"),
          };
          setCreateTime(item.createTime);
          const list = localStorage.getItem(const_prompt_code);
          localStorage.setItem(
            const_prompt_code,
            JSON.stringify(
              !!list
                ? [...JSON.parse(list).filter((i: ListItem) => i.id !== item.id), item]
                : [item]
            )
          );
          break; // Success, exit retry loop
        } catch (error: any) {
          console.error(`AI API call attempt ${attempt} failed:`, error);
          if (attempt < 3) {
            // Wait before retry (exponential backoff)
            setProgressing(95);
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          } else {
            setState("error");
          }
        }
      }
    } catch (error) {
      console.log("Failed to send messages:", error);
      setState("error");
    } finally {
      generatingRef.current = false;
    }
  };

  const handleClick = () => {
    if (!promptContent) {
      toast.error("ERROR: Prompt Not Found.");
      return;
    }
    setContent("");
    setCreateTime("");
    setState("complete");
    setOpen(true);
    const list = JSON.parse(localStorage.getItem(const_prompt_code) || "[]");
    const targetItem = (list || [])?.find((item: ListItem) => item.id === currentId);
    if (
      targetItem &&
      targetItem.createTime === moment().tz("America/Los_Angeles").format("MMM Do, YYYY(PT)")
    ) {
      setContent(targetItem.content || "");
      setCreateTime(targetItem.createTime || "");
    } else {
      handleAddAccount();
    }
  };

  const onClose = () => {
    setOpen(false);
  };

  if (!authDetail.features?.includes(FEATURE_KEY.SP_AI_ASSISTANT)) {
    return null;
  }

  return (
    <div
      style={{
        display: "inline-block",
      }}
    >
      {canHandleAddAccount ? (
        <Tooltip title={tooltipTitle}>
          <span onClick={handleClick}>
            {children || (
              <IconButton>
                <img
                  src="/icon-summeries-AI.png"
                  width={SizeMapping[size] || 24}
                  alt=""
                />
              </IconButton>
            )}
          </span>
        </Tooltip>
      ) : null}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={"lg"}
      >
        <DialogTitle className={styles.dialogHeader}>
          <div className={styles.headerAccountName}>
            {dialogTitleText}
            {account?.accountName && ` - ${account.accountName}`}
          </div>
          <div className={styles.buttonGroup}>
            {state === "complete" && (
              <div className={styles.createTime}>
                Generated Date: {createTime}
                <Tooltip
                  title="Data is auto-cached for 24 hours. All views within 24 hours display today's first-generated content. Manually refresh to get the latest data."
                  PopperProps={{ disablePortal: true }}
                >
                  <span>
                    <InfoOutlined
                      style={{
                        fontSize: 18,
                        cursor: "pointer",
                        padding: 2,
                      }}
                    />
                  </span>
                </Tooltip>
              </div>
            )}
            {state !== "thinking" && state !== "loading" && (
              <>
                {showPptBtn && (
                  <MarkdownToPPTBtn
                    material={content}
                    template="template_mber"
                  />
                )}
                <IconButton
                  style={{
                    color: "rgb(62, 177, 255)",
                  }}
                  onClick={async () => {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      await navigator.clipboard.writeText(content);
                      toast.success("copy success");
                    }
                  }}
                >
                  <ContentCopyIcon />
                </IconButton>
                <IconButton
                  style={{
                    color: "rgb(62, 177, 255)",
                  }}
                  onClick={handleAddAccount}
                >
                  <AutorenewIcon />
                </IconButton>
              </>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {(state === "complete" || state === "loading") && (
            <>
              <EXTMarkdown content={content} />
              {state === "loading" && (
                <span>
                  <svg
                    className={styles.iconSmallLoading}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="M21 12a9 9 0 11-6.219-8.56"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              )}
            </>
          )}
          <AIDialogProcess
            state={state}
            error={"Not Eligible for Assessment"}
            width={progressing}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIButton;
